import type { FieldMiddleware } from '@nestjs/graphql'

/**
 * The record a guarded field sits on: anything with an owning `userId`, as every `BaseModelDto` has.
 */
interface OwnedRecord {
  userId?: number
}

/**
 * The slice of the API's GraphQL context this reads. `config.service.ts` builds the context as `{ req }`, and
 * the passport guard on the operation puts the token user on `req.user` (see `jwt.strategy.ts`; the anonymous
 * strategy leaves an empty object there).
 */
interface OwnerOrStaffContext {
  req?: {
    user?: {
      queryUserId?: number
      isAdmin?: boolean
    }
  }
}

/**
 * Field middleware for a value only the record's owner, or staff, may read. Everyone else, signed in or not,
 * gets null for the field whichever query reached the record: it runs where the field itself resolves, so it
 * also covers the record nested inside another type and every subclass of the DTO [sc-28919].
 *
 * The owner is the record's `userId`, compared with the token's `queryUserId` - the account being acted as or
 * mimicked when there is one, which is how an assistant or a mimicking admin works on an author's behalf.
 * Staff are `isAdmin`. A request that no guard authenticated has no user, so it gets null as well.
 *
 * Returning without calling `next` is what keeps the stored value from ever being read.
 */
export const ownerOrStaffOnly: FieldMiddleware<OwnedRecord, OwnerOrStaffContext> = ({ source, context }, next) => {
  const user = context?.req?.user
  if (user?.isAdmin === true) {
    return next()
  }

  if (typeof source.userId === 'number' && source.userId === user?.queryUserId) {
    return next()
  }

  return null
}
